// Maps an AI-review / DHS issue `field` dot-path to the wizard step that
// owns that field, so the nurse can jump straight back to fix it.
//
// Keep the prefix table sorted from MOST to LEAST specific — `assessment.pain`
// must resolve before the generic `assessment` fallback.

const PREFIX_TO_STEP = [
  // Step 1 — Agency & Client
  { prefix: "agency",      step: 1, label: "Agency & Client" },
  { prefix: "client",      step: 1, label: "Agency & Client" },
  { prefix: "insurance",   step: 1, label: "Agency & Client" },
  { prefix: "physician",   step: 1, label: "Agency & Client" },

  // Step 2 — ADL Assessment
  { prefix: "adl",         step: 2, label: "ADL Assessment" },

  // Step 3 — Nursing Tasks (includes PRN, seizures, behaviors, rare condition)
  { prefix: "nursing",          step: 3, label: "Nursing Tasks" },
  { prefix: "prn",              step: 3, label: "Nursing Tasks" },
  { prefix: "seizures",         step: 3, label: "Nursing Tasks" },
  { prefix: "behaviors",        step: 3, label: "Nursing Tasks" },
  { prefix: "rare_condition",   step: 3, label: "Nursing Tasks" },

  // Step 4 — Communication & Senses
  { prefix: "communication",      step: 4, label: "Communication & Senses" },
  { prefix: "hearing_aid",        step: 4, label: "Communication & Senses" },
  { prefix: "vision_correction",  step: 4, label: "Communication & Senses" },

  // Step 5 — Medical History & Meds
  { prefix: "diagnoses",        step: 5, label: "Medical History & Meds" },
  { prefix: "medications",      step: 5, label: "Medical History & Meds" },
  { prefix: "medical_history",  step: 5, label: "Medical History & Meds" },
  { prefix: "allergies",        step: 5, label: "Medical History & Meds" },

  // Step 6 — Social & Schedules
  { prefix: "social",                 step: 6, label: "Social & Schedules" },
  { prefix: "f11136_activity_grid",   step: 6, label: "Social & Schedules" },
  { prefix: "f11136_staffing_grid",   step: 6, label: "Social & Schedules" },
  { prefix: "f11136_other_info",      step: 6, label: "Social & Schedules" },

  // Step 7 — Non-Skilled Assessment (all assessment.* paths land here)
  { prefix: "assessment",   step: 7, label: "Non-Skilled Assessment" },
  { prefix: "emergency",    step: 7, label: "Non-Skilled Assessment" },

  // Step 8 — Plan of Care (CMS-485 + clinical summary rationale)
  { prefix: "cms485",            step: 8, label: "Plan of Care" },
  { prefix: "clinical_summary",  step: 8, label: "Plan of Care" },

  // Step 10 — Triple Verification (signatures live on the final step)
  { prefix: "signatures",  step: 10, label: "Triple Verification" },
];

export function fieldToStep(field) {
  if (!field || typeof field !== "string") return null;
  const head = field.split(".")[0];
  const match = PREFIX_TO_STEP.find((p) => p.prefix === head);
  return match ? { step: match.step, label: match.label, fieldPath: field } : null;
}

// Known field-path → data-testid overrides (hand-verified from step components
// where the testid doesn't follow the generic dot→dash convention).
const TESTID_OVERRIDES = {
  "client.first_name":             "client-first",
  "client.last_name":              "client-last",
  "client.date_of_birth":          "client-dob",
  "client.forwardhealth_member_id": "client-member-id",
  "client.assessment_date":        "client-assessment-date",
  "agency.provider_number":        "agency-provider-number",
  "physician.name":                "phys-name",
  "physician.npi":                 "phys-npi",
  "nursing.part_iii.tube_bolus":   "mot-p3-tube_bolus",
  "nursing.part_iii.other_description": "mot-p3-comments",
  "nursing.part_ii.glucometer":    "mot-p2-glucometer",
  "nursing.part_iii.vitals":       "mot-p3-vitals",
  "nursing.medication_assistance.level": "mot-med-level",
  "prn.description":               "consider-prn-desc",
  "seizures.type":                 "consider-seizures-type",
  "seizures.frequency":            "consider-seizures-freq",
  "behaviors.description":         "consider-behaviors-desc",
  "rare_condition.description":    "consider-rare-desc",
  "cms485.cert_period_from":       "cms-from",
  "cms485.cert_period_to":         "cms-to",
  "signatures":                    "rn-signature-name",
  "signatures.rn_name":            "rn-signature-name",
  "signatures.rn_date":            "rn-signature-date",
  "signatures.rep_name":           "rep-signature-name",
  "signatures.rep_date":           "rep-signature-date",
};

// ADL dot-paths (`adl.bathing.notes`, `adl.bathing.pcst_level`) convert cleanly
// to `adl-bathing-notes` / `adl-bathing-pcst-level` via the generic fallback.
function genericDotToDash(field) {
  return field.replace(/_/g, "-").replace(/\./g, "-");
}

export function fieldToTestId(field) {
  if (!field || typeof field !== "string") return null;
  if (TESTID_OVERRIDES[field]) return TESTID_OVERRIDES[field];
  // Try the most-specific path first, then walk up to the head.
  const parts = field.split(".");
  for (let i = parts.length; i > 0; i--) {
    const prefix = parts.slice(0, i).join(".");
    if (TESTID_OVERRIDES[prefix]) return TESTID_OVERRIDES[prefix];
  }
  return genericDotToDash(field);
}
